<template>
  <div class="space-y-6">
    <!-- Add New Domain Card -->
    <Card>
      <h3 class="text-lg font-medium text-text mb-4">Add Domain</h3>
      <div class="max-w-md">
        <label for="domain" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Domain Name
        </label>
        <div class="mt-1 flex rounded-md shadow-sm">
          <input
            id="domain"
            v-model="domainInput"
            type="text"
            placeholder="acme.com"
            class="flex-1 min-w-0 block w-full px-3 py-2 rounded-none rounded-l-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            @keyup.enter="handleAddDomain"
          />
          <!-- eslint-disable-next-line asab-design-system/no-raw-button-card-markup -- attached input-group action button, not a standalone Button -->
          <button
            @click="handleAddDomain"
            :disabled="!domainInput || loading"
            class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-r-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {{ loading ? 'Adding...' : 'Add' }}
          </button>
        </div>
        <p class="mt-2 text-sm text-gray-500">
          Verify your domains to enable Enterprise JIT provisioning.
        </p>
      </div>
    </Card>

    <!-- Domains List -->
    <Card padding="none">
      <template #header>
        <div class="px-6 py-4">
          <h3 class="text-lg font-medium text-text">Organization Domains</h3>
        </div>
      </template>

      <div v-if="domains.length > 0" class="divide-y divide-gray-200 dark:divide-gray-700">
        <div v-for="domain in domains" :key="domain.id" class="p-6">
          <div class="flex items-center justify-between mb-4">
            <div class="flex items-center space-x-3">
              <div
                class="flex-shrink-0 h-10 w-10 rounded-full flex items-center justify-center"
                :class="
                  domain.is_verified
                    ? 'bg-green-100 dark:bg-green-900/30'
                    : 'bg-amber-100 dark:bg-amber-900/30'
                "
              >
                <svg
                  v-if="domain.is_verified"
                  class="h-6 w-6 text-green-600 dark:text-green-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                <svg
                  v-else
                  class="h-6 w-6 text-amber-600 dark:text-amber-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <div>
                <h4 class="text-sm font-medium text-gray-900 dark:text-white">
                  {{ domain.domain }}
                </h4>
                <p class="text-xs text-gray-500">Added on {{ formatDate(domain.created_at) }}</p>
              </div>
            </div>

            <div class="flex items-center space-x-2">
              <span
                class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                :class="
                  domain.is_verified
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                    : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
                "
              >
                {{ domain.is_verified ? 'Verified' : 'Pending Verification' }}
              </span>
              <button
                @click="handleRemoveDomain(domain)"
                class="text-gray-400 hover:text-red-600 transition-colors"
                title="Remove Domain"
              >
                <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </button>
            </div>
          </div>

          <!-- Verification Instructions -->
          <div
            v-if="!domain.is_verified"
            class="bg-blue-50 dark:bg-blue-900/20 rounded-md p-4 mt-2"
          >
            <div class="flex">
              <div class="flex-shrink-0">
                <svg class="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fill-rule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clip-rule="evenodd"
                  />
                </svg>
              </div>
              <div class="ml-3">
                <p class="text-sm text-blue-700 dark:text-blue-300">
                  To verify ownership, please add the following TXT record to your DNS settings:
                </p>
                <div class="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <span
                      class="block text-xs font-semibold text-blue-800 dark:text-blue-200 uppercase"
                      >Host</span
                    >
                    <code
                      class="mt-1 block p-2 bg-white dark:bg-gray-900 rounded text-xs font-mono border border-blue-200 dark:border-blue-800"
                    >
                      @
                    </code>
                  </div>
                  <div>
                    <span
                      class="block text-xs font-semibold text-blue-800 dark:text-blue-200 uppercase"
                      >Value</span
                    >
                    <div
                      class="mt-1 flex items-center justify-between p-2 bg-white dark:bg-gray-900 rounded border border-blue-200 dark:border-blue-800"
                    >
                      <code class="text-xs font-mono break-all text-blue-900 dark:text-blue-100">
                        asab-verification={{ domain.verification_token }}
                      </code>
                      <button
                        @click="copyValue(`asab-verification=${domain.verification_token}`)"
                        class="ml-2 text-blue-600 hover:text-blue-800 dark:text-blue-400"
                        title="Copy"
                      >
                        <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
                <div class="mt-4 flex justify-end">
                  <Button size="sm" :disabled="loading" @click="handleVerifyDomain(domain.id)">
                    Verify Now
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="px-6 py-10 text-center text-gray-500 dark:text-gray-400">
        No domains added yet.
      </div>
    </Card>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, onMounted } from 'vue';
  import { useOrganization } from '../composables';
  import type { OrganizationDomain } from '../types';
  import { Card, Button } from '@asabdev/asab-ui';

  const {
    domains: storeDomains,
    addDomain,
    verifyDomain,
    removeDomain,
    fetchDomains,
    isLoading: loading,
    initialize,
  } = useOrganization();

  onMounted(async () => {
    await initialize();
    await fetchDomains();
  });

  const domainInput = ref('');

  const domains = computed(() => {
    return storeDomains.value || [];
  });

  const handleAddDomain = async () => {
    if (!domainInput.value) return;
    try {
      await addDomain(domainInput.value);
      domainInput.value = '';
    } catch (error) {
      console.error('Failed to add domain:', error);
    }
  };

  const handleVerifyDomain = async (domainId: string) => {
    try {
      await verifyDomain(domainId);
    } catch (error) {
      console.error('Failed to verify domain:', error);
    }
  };

  const handleRemoveDomain = async (domain: OrganizationDomain) => {
    if (!confirm(`Are you sure you want to remove the domain ${domain.domain}?`)) {
      return;
    }
    try {
      await removeDomain(domain.id);
    } catch (error) {
      console.error('Failed to remove domain:', error);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const copyValue = (value: string) => {
    navigator.clipboard.writeText(value);
  };
</script>
